// Tabbed content panel for a delivery record ([3.3], ws-g18no) — five tabs over
// [2.1]'s content + insights fields: a rendered HTML Preview, the Plain Text /
// HTML source / Raw MIME blobs as mono code blocks, and an Insights tab that
// groups the record's DeliveryInsight[] findings into three named tiers, each
// finding a collapsible row with a tone icon. Replaces the single "Provider
// response" CodePanel the detail screen carried before. Radix Tabs/Collapsible
// are driven directly (both already installed) with square-edged, token-based
// styling to match the repo's Badge/Card/CodePanel vocabulary.
import { useState } from 'react';
import * as Tabs from '@radix-ui/react-tabs';
import * as Collapsible from '@radix-ui/react-collapsible';
import {
	AlertTriangle,
	Check,
	ChevronRight,
	Info,
	type LucideIcon,
} from 'lucide-react';

import type {
	DeliveryInsight,
	DeliveryInsightTier,
	DeliveryRecord,
} from '@/api/delivery-log/mock';
import { Badge, type BadgeStatus } from '@/components/ui/badge';

type TabKey = 'preview' | 'plaintext' | 'html' | 'raw' | 'insights';

const TABS: { key: TabKey; label: string }[] = [
	{ key: 'preview', label: 'Preview' },
	{ key: 'plaintext', label: 'Plain Text' },
	{ key: 'html', label: 'HTML' },
	{ key: 'raw', label: 'Raw' },
	{ key: 'insights', label: 'Insights' },
];

/** Ordered tiers + their display label, badge color, and tone icon. */
const TIER_ORDER: {
	tier: DeliveryInsightTier;
	label: string;
	status: BadgeStatus;
	icon: LucideIcon;
}[] = [
	{
		tier: 'needs-attention',
		label: 'Needs Attention',
		status: 'warning',
		icon: AlertTriangle,
	},
	{
		tier: 'possible-improvement',
		label: 'Possible Improvements',
		status: 'info',
		icon: Info,
	},
	{
		tier: 'doing-great',
		label: 'Doing Great',
		status: 'success',
		icon: Check,
	},
];

/** Icon-tile tint per tier — mirrors the pill color family (EventTimeline). */
const TIER_TILE: Record<DeliveryInsightTier, string> = {
	'needs-attention': 'border-warning/40 bg-warning/10 text-warning',
	'possible-improvement': 'border-info/40 bg-info/10 text-info',
	'doing-great': 'border-success/40 bg-success/10 text-success',
};

const TAB_TRIGGER_CLASS =
	'-mb-px border-b-2 border-transparent px-3 py-2 text-xs font-medium uppercase tracking-wide text-muted-foreground transition-colors hover:text-foreground data-[state=active]:border-info data-[state=active]:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring';

/** Shared bordered mono code block (matches CodePanel's pre styling). */
function CodeBlock({ content }: { content: string }) {
	return (
		<pre className="max-h-96 overflow-auto border border-border bg-muted/50 px-4 py-3 font-mono text-xs leading-relaxed text-foreground">
			{content}
		</pre>
	);
}

/** One collapsible insight row: tone icon tile + message, expand for detail. */
function InsightRow({
	insight,
	icon: Icon,
	tier,
}: {
	insight: DeliveryInsight;
	icon: LucideIcon;
	tier: DeliveryInsightTier;
}) {
	const [open, setOpen] = useState(false);

	return (
		<Collapsible.Root open={open} onOpenChange={setOpen} asChild>
			<li className="border border-border bg-card">
				<Collapsible.Trigger className="flex w-full items-center gap-3 px-3 py-2.5 text-left transition-colors hover:bg-accent focus:outline-none focus-visible:ring-2 focus-visible:ring-ring">
					<span
						className={`flex size-7 shrink-0 items-center justify-center border [&_svg]:size-4 ${TIER_TILE[tier]}`}
					>
						<Icon aria-hidden="true" />
					</span>
					<span className="min-w-0 flex-1 truncate text-sm text-foreground">
						{insight.message}
					</span>
					<ChevronRight
						className="size-4 shrink-0 text-muted-foreground transition-transform data-[state=open]:rotate-90"
						data-state={open ? 'open' : 'closed'}
						aria-hidden="true"
					/>
				</Collapsible.Trigger>
				<Collapsible.Content>
					<div className="border-t border-border px-3 py-2.5 pl-[3.25rem] text-sm text-muted-foreground">
						{insight.message}
					</div>
				</Collapsible.Content>
			</li>
		</Collapsible.Root>
	);
}

/** Insights tab body — groups findings into the three tiers in fixed order. */
function InsightsPanel({ insights }: { insights: DeliveryInsight[] }) {
	if (insights.length === 0) {
		return (
			<p className="border border-border bg-muted/50 px-4 py-6 text-center text-sm text-muted-foreground">
				No insights for this delivery.
			</p>
		);
	}

	return (
		<div className="flex flex-col gap-5">
			{TIER_ORDER.map(({ tier, label, status, icon }) => {
				const rows = insights.filter((i) => i.tier === tier);
				if (rows.length === 0) return null;

				return (
					<section key={tier} aria-label={label}>
						<div className="mb-2 flex items-center gap-2">
							<Badge status={status}>{label}</Badge>
							<span className="text-xs tabular-nums text-muted-foreground">
								{rows.length}
							</span>
						</div>
						<ul className="flex flex-col gap-1.5">
							{rows.map((insight, i) => (
								<InsightRow
									key={`${tier}-${i}`}
									insight={insight}
									icon={icon}
									tier={tier}
								/>
							))}
						</ul>
					</section>
				);
			})}
		</div>
	);
}

export interface ContentTabsProps {
	record: DeliveryRecord;
}

export function ContentTabs({ record }: ContentTabsProps) {
	return (
		<section aria-label="Message content">
			<h2 className="mb-3 text-xs font-medium uppercase tracking-wide text-muted-foreground">
				Content
			</h2>
			<Tabs.Root defaultValue="preview">
				<Tabs.List
					aria-label="Message content tabs"
					className="flex items-center gap-1 border-b border-border"
				>
					{TABS.map((tab) => (
						<Tabs.Trigger
							key={tab.key}
							value={tab.key}
							className={TAB_TRIGGER_CLASS}
						>
							{tab.label}
						</Tabs.Trigger>
					))}
				</Tabs.List>

				<div className="mt-4">
					<Tabs.Content value="preview" className="focus:outline-none">
						{/* MOCK-generated HTML (not user input) — safe to render here. */}
						<div
							className="max-h-96 overflow-auto border border-border bg-card px-4 py-3 text-sm text-foreground [&_a]:text-info [&_a]:underline"
							dangerouslySetInnerHTML={{ __html: record.contentHtml }}
						/>
					</Tabs.Content>

					<Tabs.Content value="plaintext" className="focus:outline-none">
						<CodeBlock content={record.contentPlainText} />
					</Tabs.Content>

					<Tabs.Content value="html" className="focus:outline-none">
						<CodeBlock content={record.contentHtml} />
					</Tabs.Content>

					<Tabs.Content value="raw" className="focus:outline-none">
						<CodeBlock content={record.contentRaw} />
					</Tabs.Content>

					<Tabs.Content value="insights" className="focus:outline-none">
						<InsightsPanel insights={record.insights} />
					</Tabs.Content>
				</div>
			</Tabs.Root>
		</section>
	);
}
